import React from 'react';
import Proptypes from 'prop-types';
import { Button, Callout, Divider, Link, ProgressSteps, Text, Title, Icon } from '@homeexchange/design';

import i18n from '../../../i18n';
import User from '../../../models/User';

export class UserVerification extends React.PureComponent {
    static propTypes = {
        user: Proptypes.instanceOf(User).isRequired,
        auth: Proptypes.shape({
            accessToken: Proptypes.string.isRequired
        }).isRequired,
        showPersona: Proptypes.func.isRequired
    };

    getHomeToVerify(homes) {
        homes.sort((a, b) => (a.id <= b.id ? -1 : 1));
        return homes.find((home) => !home.isVerified());
    }

    stepOne(isHomeQualified) {
        const { user } = this.props;

        if (!user) {
            return;
        }

        const home = this.getHomeToVerify(user.get('homes'));
        const createOrUpdateHomePath = home
            ? i18n.t('url:home_edit', { id: home.id })
            : i18n.t('home:home.create.url');

        const stepTitle = (
            <div className="step-title">{i18n.t('user:page.verifPage.completeHome.title')}</div>
        );

        return (
            <div className="step-content">
                {stepTitle}
                <div className="step-info">
                    {!isHomeQualified && (
                        <>
                            <Text
                                size="sm"
                                dangerouslySetInnerHTML={{
                                    __html: i18n.t('user:page.verifPage.completeHome.desc')
                                }}
                            />

                            {home && (
                                <>
                                    <Divider space="small" />
                                    <Text>{home.get('address')}</Text>
                                    <Divider space="small" />
                                </>
                            )}

                            <Button
                                className="button-step"
                                onClick={() => (location.href = createOrUpdateHomePath)}
                            >
                                {i18n.t('user:page_verification_completeNewHome_title')}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    stepTwo(stepDisabled) {
        const { user } = this.props;

        const stepTitle = (
            <div className="step-title">{i18n.t('user:verify_account.step.verify_phone.title')}</div>
        );

        return (
            <div className="step-content">
                {stepTitle}
                {!stepDisabled && (
                    <div className="step-info">
                        <Text
                            size="sm"
                            dangerouslySetInnerHTML={{
                                __html: i18n.t('user:page_verification_verifPhone_desc')
                            }}
                        ></Text>
                        <Button
                            className="button-step"
                            onClick={() =>
                                (location.href = i18n.t('url:user_edit', { id: user.id }) + '#anchor-phone')
                            }
                        >
                            {i18n.t('user:global_addPhoneNumber')}
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    stepThree(stepDisabled) {
        const { user, showPersona } = this.props;

        const home = this.getHomeToVerify(user.get('homes'));

        const stepTitle = (
            <div className="step-title">{i18n.t('user:page_verification_verifDocs_title')}</div>
        );

        return (
            <div className="step-content">
                {stepTitle}
                {!stepDisabled && (
                    <div className="step-info">
                        <Text
                            className="user-verification-step-three-text"
                            size="sm"
                            dangerouslySetInnerHTML={{
                                __html: i18n.t('user:page_verification_verifDocs_desc')
                            }}
                        ></Text>

                        {home && (
                            <>
                                <Divider space="small" />
                                <Text>{home.get('address')}</Text>
                                <Divider space="small" />
                            </>
                        )}
                        {home?.isOffline() && (
                            <Callout status="warning">
                                <Text
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.t('user:page_verification_verifDocs_offlineCallout_body')
                                    }}
                                />
                                <Link href={i18n.t('url:home_edit', { id: home.id })}>
                                    {i18n.t('user:page_verification_verifDocs_offlineCallout_link')}
                                </Link>
                            </Callout>
                        )}

                        <Button isDisabled={home?.isOffline()} className="button-step" onClick={showPersona}>
                            {i18n.t('user:global_verifyDocuments')}
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { user } = this.props;

        if (!user) {
            return;
        }

        const home = this.getHomeToVerify(user.get('homes'));
        const isHomeQualified = home && home.get('completion_rate') >= User.ENOUGH_COMPLETION_RATE;
        const isPhoneVerified = user.isPhoneVerified();
        const stepDisabled = !isHomeQualified || !isPhoneVerified;

        return (
            <div className="container">
                <div className="row">
                    <div className="col-md-7">
                        <div className="verification-title mb-2">
                            <Title isImpact={true} hasHeaderTag={true} level={2}>
                                {i18n.t('user:verify_account.title')}
                            </Title>
                        </div>

                        <Text className="verification-desc" size="md">
                            {i18n.t('user:page_verification_subheader')}
                        </Text>

                        <div className="verification-faq">
                            <Link color="secondary" target="_blank" href={i18n.t('url:url.pending_faq')}>
                                {i18n.t('user:verify_account.faq_text')}
                            </Link>
                            <Icon name="ArrowUpRightSquare" />
                        </div>

                        <div className="verification-steps">
                            <ProgressSteps
                                className="progress-steps"
                                orientation="vertical"
                                steps={[
                                    {
                                        content: this.stepOne(isHomeQualified),
                                        isDone: isHomeQualified
                                    },
                                    {
                                        content: this.stepTwo(!isHomeQualified || isPhoneVerified),
                                        isDone: isPhoneVerified,
                                        isDisabled: !isHomeQualified || isPhoneVerified
                                    },
                                    {
                                        content: this.stepThree(stepDisabled),
                                        isDisabled: !isHomeQualified || !isPhoneVerified
                                    }
                                ]}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default UserVerification;
